import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * Quick-pick chips for the Due Date field (PRD Phase 2 quick win:
 * Today / Tomorrow / This Week / Next Week / No Date). Sits below the
 * `DateInput` and writes ISO `YYYY-MM-DD` into the same form-state slot.
 *
 * "This Week" snaps to the upcoming Friday; "Next Week" snaps to next
 * Friday — they're the conventional end-of-week deadlines the PRD
 * sketch shows for clinical workflow.
 */
interface DueDateQuickPicksProps {
  readonly value: string;
  readonly disabled?: boolean;
  readonly onPick: (next: string) => void;
}

type PickKey = 'TODAY' | 'TOMORROW' | 'THIS_WEEK' | 'NEXT_WEEK' | 'NONE';

const PICKS: readonly PickKey[] = ['TODAY', 'TOMORROW', 'THIS_WEEK', 'NEXT_WEEK', 'NONE'];

export function DueDateQuickPicks({
  value,
  disabled = false,
  onPick,
}: DueDateQuickPicksProps): ReactElement {
  const { t } = useTranslation();

  const selected: PickKey | null = matchPick(value);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PICKS.map((key) => {
        const isActive = selected === key;
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => {
              onPick(resolvePick(key));
            }}
            className={cn(
              'text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border transition-colors',
              isActive
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {t(`patient.tasks.dueQuickPick.${key}`)}
          </button>
        );
      })}
    </div>
  );
}

function resolvePick(key: PickKey): string {
  if (key === 'NONE') return '';
  const today = startOfToday();
  if (key === 'TODAY') return toIso(today);
  if (key === 'TOMORROW') {
    const next = new Date(today);
    next.setDate(next.getDate() + 1);
    return toIso(next);
  }
  // Snap "this week" / "next week" to the upcoming Friday (locale-stable
  // end-of-week marker for clinical workflows).
  const friday = upcomingFriday(today);
  if (key === 'THIS_WEEK') return toIso(friday);
  const nextFriday = new Date(friday);
  nextFriday.setDate(nextFriday.getDate() + 7);
  return toIso(nextFriday);
}

function matchPick(value: string): PickKey | null {
  if (value.length === 0) return 'NONE';
  if (value === resolvePick('TODAY')) return 'TODAY';
  if (value === resolvePick('TOMORROW')) return 'TOMORROW';
  if (value === resolvePick('THIS_WEEK')) return 'THIS_WEEK';
  if (value === resolvePick('NEXT_WEEK')) return 'NEXT_WEEK';
  return null;
}

function startOfToday(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function upcomingFriday(from: Date): Date {
  // 0 = Sunday … 5 = Friday … 6 = Saturday. If today is already Friday,
  // return today; if it's Saturday, jump to next Friday.
  const friday = new Date(from);
  const dayOfWeek = friday.getDay();
  const delta = (5 - dayOfWeek + 7) % 7;
  friday.setDate(friday.getDate() + delta);
  return friday;
}

function toIso(date: Date): string {
  const yyyy = String(date.getFullYear()).padStart(4, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
